import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import {
  obtenerCuentas, crearCuenta, transferir, obtenerMovimientosCuenta,
  TIPOS_CUENTA, iconoTipo,
} from '../services/cuentasService';
import { mensajeAmigable } from '../utils/errores';
import { aNumero } from '../utils/validacion';
import { useToast } from '../context/ToastContext';
import CampoNumero from './CampoNumero';
import EditarCuentaModal from './EditarCuentaModal';
import EditarTransferenciaModal from './EditarTransferenciaModal';
import EditarSaldoInicialModal from './EditarSaldoInicialModal';

const fmt = (n) => {
  const v = Number(n || 0);
  const signo = v < 0 ? '-' : '';
  return `${signo}${simboloMoneda()} ${Math.abs(v).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

const ETIQUETA_MOV = {
  venta: 'Venta', gasto: 'Gasto', saldo_inicial: 'Saldo inicial',
  transferencia_entrada: 'Transferencia recibida', transferencia_salida: 'Transferencia enviada',
  ajuste: 'Ajuste', ingreso_libre: 'Venta libre',
};

export default function CuentasView({ onVolver }) {
  const { mostrarToast } = useToast();
  const [cuentas, setCuentas] = useState(null);
  const [error, setError] = useState(null);
  const [nueva, setNueva] = useState({ nombre: '', tipo: 'banco', saldoInicial: '' });
  const [ocupado, setOcupado] = useState(false);
  const [transfer, setTransfer] = useState({ origen: '', destino: '', monto: '', descripcion: '' });
  const [detalle, setDetalle] = useState(null); // { cuenta, movimientos }
  const [editando, setEditando] = useState(null); // cuenta que se está editando
  const [editarTransfRef, setEditarTransfRef] = useState(null); // transferencia a corregir
  const [editarSaldoIni, setEditarSaldoIni] = useState(null); // { cuentaId, nombre, monto }

  const cargar = async () => {
    setError(null);
    try {
      setCuentas(await obtenerCuentas());
    } catch (e) {
      setError(mensajeAmigable(e));
    }
  };
  useEffect(() => { cargar(); }, []);

  const totalDisponible = useMemo(
    () => (cuentas || []).filter((c) => c.tipo !== 'tarjeta_credito').reduce((s, c) => s + Number(c.saldo || 0), 0),
    [cuentas]
  );
  const totalDeuda = useMemo(
    () => (cuentas || []).filter((c) => c.tipo === 'tarjeta_credito' && Number(c.saldo) < 0).reduce((s, c) => s + Number(c.saldo || 0), 0),
    [cuentas]
  );

  const agregarCuenta = async (e) => {
    e.preventDefault();
    if (!nueva.nombre.trim()) { mostrarToast('Escribe el nombre de la cuenta', 'error'); return; }
    setOcupado(true);
    try {
      await crearCuenta(nueva);
      mostrarToast('Cuenta creada ✅', 'exito');
      setNueva({ nombre: '', tipo: 'banco', saldoInicial: '' });
      cargar();
    } catch (err) {
      mostrarToast(mensajeAmigable(err), 'error');
    } finally { setOcupado(false); }
  };

  const hacerTransferencia = async (e) => {
    e.preventDefault();
    if (!transfer.origen || !transfer.destino) { mostrarToast('Elige cuenta origen y destino', 'error'); return; }
    if (transfer.origen === transfer.destino) { mostrarToast('Deben ser cuentas distintas', 'error'); return; }
    if (!(aNumero(transfer.monto) > 0)) { mostrarToast('Escribe el monto a transferir (solo números, mayor que 0)', 'error'); return; }
    setOcupado(true);
    try {
      await transferir(transfer);
      mostrarToast('Transferencia registrada ✅', 'exito');
      setTransfer({ origen: '', destino: '', monto: '', descripcion: '' });
      cargar();
    } catch (err) {
      mostrarToast(mensajeAmigable(err), 'error');
    } finally { setOcupado(false); }
  };

  const verMovimientos = async (c) => {
    try {
      setDetalle({ cuenta: c, movimientos: await obtenerMovimientosCuenta(c.id) });
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); }
  };

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">🏦 Cuentas</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ backgroundColor: '#ECFDF5', borderRadius: '12px', padding: '16px', border: '1px solid #6EE7B7' }}>
          <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#065F46', fontWeight: 600 }}>💰 Dinero disponible (todas las cuentas)</p>
          <p style={{ margin: 0, fontSize: '26px', fontWeight: 'bold', color: 'var(--k-verde)' }}>{fmt(totalDisponible)}</p>
        </div>
        {totalDeuda < 0 && (
          <div style={{ backgroundColor: '#FEF2F2', borderRadius: '12px', padding: '16px', border: '1px solid #FCA5A5' }}>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#991B1B', fontWeight: 600 }}>🪙 Deuda en tarjetas</p>
            <p style={{ margin: 0, fontSize: '26px', fontWeight: 'bold', color: '#DC2626' }}>{fmt(totalDeuda)}</p>
          </div>
        )}
      </div>

      {/* Lista de cuentas */}
      {!cuentas && !error && <div style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>⏳ Cargando cuentas...</div>}
      {error && <p style={{ textAlign: 'center', padding: '24px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
      {cuentas && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {cuentas.map((c) => {
            const esDeuda = c.tipo === 'tarjeta_credito';
            const negativo = Number(c.saldo) < 0;
            return (
              <div key={c.id} className="k-card" style={{ padding: '16px' }}>
                <p style={{ margin: '0 0 2px', fontWeight: 700, color: 'var(--k-ink)' }}>{iconoTipo(c.tipo)} {c.nombre}</p>
                <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--k-faint)' }}>{TIPOS_CUENTA.find((t) => t.valor === c.tipo)?.etiqueta}</p>
                <p style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 700, fontFamily: "'DM Mono', monospace", color: negativo ? '#E02424' : 'var(--k-verde)' }}>
                  {fmt(c.saldo)}
                </p>
                {esDeuda && negativo && <p style={{ margin: '-6px 0 8px', fontSize: '11px', color: '#DC2626' }}>Deuda actual</p>}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button onClick={() => verMovimientos(c)} style={{ padding: '5px 10px', backgroundColor: '#F3E8FF', color: '#7C3AED', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>📜 Movimientos</button>
                  <button onClick={() => setEditando(c)} style={{ padding: '5px 10px', background: 'none', border: '1px solid var(--color-primario,#DB2777)', color: 'var(--color-primario,#DB2777)', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>✏️ Editar</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Nueva cuenta + transferencia */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        <form onSubmit={agregarCuenta} style={{ backgroundColor: '#FFF5F7', borderRadius: '12px', padding: '16px', border: '1px solid #FBCFE8' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>➕ Nueva cuenta</p>
          <input value={nueva.nombre} disabled={ocupado} onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })} style={{ ...input, marginBottom: '10px' }} placeholder="Nombre (ej. BAC ahorro, POS Visa)" />
          <select value={nueva.tipo} disabled={ocupado} onChange={(e) => setNueva({ ...nueva, tipo: e.target.value })} style={{ ...input, marginBottom: '10px' }}>
            {TIPOS_CUENTA.map((t) => <option key={t.valor} value={t.valor}>{t.icono} {t.etiqueta}</option>)}
          </select>
          <CampoNumero valor={nueva.saldoInicial} onChange={(v) => setNueva({ ...nueva, saldoInicial: v })} deshabilitado={ocupado}
            negativos prefijo="L" placeholder="Saldo actual (opcional)" estilo={{ ...input, marginBottom: '10px' }} />
          <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#9CA3AF' }}>
            Para tarjeta de crédito, si ya debes, escribe el saldo en negativo (ej. -5000).
          </p>
          <button type="submit" disabled={ocupado} style={{ padding: '10px 24px', backgroundColor: ocupado ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
            {ocupado ? '⏳...' : 'Crear cuenta'}
          </button>
        </form>

        <form onSubmit={hacerTransferencia} style={{ backgroundColor: '#EFF6FF', borderRadius: '12px', padding: '16px', border: '1px solid #BFDBFE' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 'bold', color: '#2563EB' }}>🔄 Transferir entre cuentas</p>
          <select value={transfer.origen} disabled={ocupado} onChange={(e) => setTransfer({ ...transfer, origen: e.target.value })} style={{ ...input, marginBottom: '10px' }}>
            <option value="">Desde... (sale el dinero)</option>
            {(cuentas || []).map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre} ({fmt(c.saldo)})</option>)}
          </select>
          <select value={transfer.destino} disabled={ocupado} onChange={(e) => setTransfer({ ...transfer, destino: e.target.value })} style={{ ...input, marginBottom: '10px' }}>
            <option value="">Hacia... (entra el dinero)</option>
            {(cuentas || []).map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
          </select>
          <CampoNumero valor={transfer.monto} onChange={(v) => setTransfer({ ...transfer, monto: v })} deshabilitado={ocupado}
            min={0} prefijo="L" placeholder="Monto a transferir" estilo={{ ...input, marginBottom: '10px' }} />
          <input value={transfer.descripcion} disabled={ocupado} onChange={(e) => setTransfer({ ...transfer, descripcion: e.target.value })} style={{ ...input, marginBottom: '10px' }} placeholder="Nota (ej. Pago tarjeta, retiro caja)" />
          <button type="submit" disabled={ocupado} style={{ padding: '10px 24px', backgroundColor: ocupado ? '#9CA3AF' : '#2563EB', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
            {ocupado ? '⏳...' : 'Transferir'}
          </button>
        </form>
      </div>

      {/* Modal de movimientos */}
      {detalle && (
        <div onClick={() => setDetalle(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '520px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h3 style={{ margin: 0, color: 'var(--color-primario,#DB2777)' }}>{iconoTipo(detalle.cuenta.tipo)} {detalle.cuenta.nombre}</h3>
              <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>✕</button>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: '20px', fontWeight: 'bold', color: Number(detalle.cuenta.saldo) < 0 ? '#DC2626' : 'var(--k-verde)' }}>
              Saldo actual: {fmt(detalle.cuenta.saldo)}
            </p>
            {detalle.movimientos.length === 0 && <p style={{ color: '#9CA3AF', fontSize: '13px' }}>Sin movimientos todavía.</p>}
            {detalle.movimientos.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px', gap: '8px' }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, color: '#111827', fontWeight: 600 }}>{ETIQUETA_MOV[m.tipo] || m.tipo}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                    {new Date(m.creado_en).toLocaleDateString('es-HN')}{m.descripcion ? ` · ${m.descripcion}` : ''}
                  </p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div>
                    <span style={{ fontWeight: 'bold', color: Number(m.monto) < 0 ? '#DC2626' : 'var(--k-verde)' }}>
                      {Number(m.monto) < 0 ? '' : '+'}{fmt(m.monto)}
                    </span>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9CA3AF' }}>Saldo: {fmt(m.saldo_resultante)}</p>
                  </div>
                  {(m.tipo === 'transferencia_salida' || m.tipo === 'transferencia_entrada') && m.referencia && (
                    <button onClick={() => setEditarTransfRef(m.referencia)} title="Corregir esta transferencia"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>✏️</button>
                  )}
                  {m.tipo === 'saldo_inicial' && (
                    <button onClick={() => setEditarSaldoIni({ cuentaId: detalle.cuenta.id, nombre: detalle.cuenta.nombre, monto: m.monto })} title="Corregir el saldo inicial"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>✏️</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editando && (
        <EditarCuentaModal
          cuenta={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={cargar}
        />
      )}

      {editarTransfRef && (
        <EditarTransferenciaModal
          referencia={editarTransfRef}
          onCerrar={() => setEditarTransfRef(null)}
          onGuardado={() => { setDetalle(null); cargar(); }}
        />
      )}

      {editarSaldoIni && (
        <EditarSaldoInicialModal
          cuentaId={editarSaldoIni.cuentaId}
          cuentaNombre={editarSaldoIni.nombre}
          montoActual={editarSaldoIni.monto}
          onCerrar={() => setEditarSaldoIni(null)}
          onGuardado={() => { setDetalle(null); cargar(); }}
        />
      )}
    </div>
  );
}
